import React from "react";
import { Box, Typography } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import FundsPeriodTotalsBar from "./FundsPeriodTotalsBar";
import { formatFundsUsd, PERIOD_TOTAL_LABELS } from "../../Utils/fundsPeriodTotals";

const shellSx = {
  borderRadius: "18px",
  border: "1px solid rgba(42, 245, 152, 0.16)",
  bgcolor: "background.paper",
  overflow: "hidden",
  background:
    "linear-gradient(135deg, rgba(42, 245, 152, 0.08) 0%, rgba(45,45,45,0.98) 28%, rgba(30,30,30,1) 100%)",
  boxShadow: "0 10px 28px rgba(0, 0, 0, 0.28)",
};

const FundsPageShell = ({
  title,
  headerMeta,
  totalsProps,
  headerExtra,
  toolbar,
}) => {
  const totalAmount =
    (totalsProps?.totals?.individual?.total || 0) +
    (totalsProps?.totals?.corporate?.total || 0);
  const totalCount =
    (totalsProps?.totals?.individual?.count || 0) +
    (totalsProps?.totals?.corporate?.count || 0);
  const routingTotal = totalsProps?.totals?.routing?.total || 0;
  const routingCount = totalsProps?.totals?.routing?.count || 0;
  const periodLabel =
    totalsProps?.startDate && totalsProps?.endDate
      ? `${totalsProps.startDate.format("DD/MM/YYYY")} - ${totalsProps.endDate.format("DD/MM/YYYY")}`
      : "";
  const labels =
    totalsProps?.labels ||
    PERIOD_TOTAL_LABELS[totalsProps?.type] ||
    PERIOD_TOTAL_LABELS.deposit;
  const totalLabel = labels.totalLabel;
  const countLabel = labels.countLabel;
  const splitCaption = labels.splitCaption;

  return (
  <Box sx={shellSx}>
    <Box
      sx={{
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 2.5 },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(260px, 1fr) minmax(250px, 340px) minmax(380px, 460px)" },
        gap: { xs: 2, lg: 2.25 },
        alignItems: "stretch",
      }}
    >
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography
          sx={{
            fontSize: { xs: "22px", md: "26px" },
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.2,
            letterSpacing: -0.4,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: 13, lineHeight: 1.4 }}>
          {periodLabel || "Select a period"} · {splitCaption}
        </Typography>
        {headerMeta ? (
          <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
            {headerMeta}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          borderRadius: "14px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          background: "linear-gradient(135deg, rgba(42,245,152,0.11), rgba(0,0,0,0.22))",
          px: 2,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#9bd9bf", fontSize: 11, fontWeight: 800, letterSpacing: 0.7, textTransform: "uppercase" }}>
            {totalLabel}
          </Typography>
          <Typography
            sx={{
              mt: 0.65,
              color: "#fff",
              fontSize: { xs: 24, md: 28 },
              fontWeight: 850,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatFundsUsd(totalAmount)}
          </Typography>
          <Typography sx={{ mt: 0.75, color: "#8a8a8a", fontSize: 12 }}>
            {totalCount} {countLabel}
          </Typography>
          {routingTotal > 0 && (
            <Typography sx={{ mt: 0.35, color: "#8a8a8a", fontSize: 11 }}>
              Excl. Capital Routing: {formatFundsUsd(routingTotal)} ({routingCount})
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "13px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(42, 245, 152, 0.13)",
            color: "#2AF598",
            flexShrink: 0,
          }}
        >
          <AccountBalanceWalletIcon sx={{ fontSize: 22 }} />
        </Box>
      </Box>

      <Box
        sx={{
          minWidth: 0,
        }}
      >
        <FundsPeriodTotalsBar {...totalsProps} />
      </Box>
    </Box>

    {headerExtra ? (
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(255, 255, 255, 0.02)",
        }}
      >
        {headerExtra}
      </Box>
    ) : null}

    {toolbar ? (
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.75, md: 2 },
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(0, 0, 0, 0.12)",
        }}
      >
        {toolbar}
      </Box>
    ) : null}
  </Box>
  );
};

export default FundsPageShell;
